/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useRef, useState } from "react";
import {
	motion,
	useAnimation,
	useInView,
	useReducedMotion,
} from "motion/react";
import { Link } from "react-router-dom";
import { useGsapScrollParallax } from "@/hooks/useGsapScrollParallax";

import { useTranslation } from "react-i18next";
import { Navigation } from "@/components/customCopmonent/landingSection/navigation";
import { FooterSection } from "@/components/customCopmonent/landingSection/footer-section";
import {
	PageShell,
	SectionDivider,
	SectionWrapper,
} from "./FoundersMessage.page-primitives";
import DotGrid from "@/components/customCopmonent/DotGrid";
import { PageHeaderHero, PAGE_HERO_CLASS } from "@/components/customCopmonent/Text/PageHeaderHero";
import FoundersMessageComponent from "@/components/customCopmonent/FoundersMessageComponent";
import Seo from "@/components/Seo";

const FoundersMessage = () => {
	const { t } = useTranslation();

	const pageData = {
		title: t("aboutPages.foundersMessage.hero.title"),
		bgimage: "/images/headerPhotos/foundermsg.jpg",

		image: "/images/ManagementPhoto/ve.jpeg",
		description: t("aboutPages.foundersMessage.hero.description"),
		// Short, page-specific hero line — the longer `description` above still
		// heads the message section further down the page.
		tagline: t("aboutPages.foundersMessage.hero.tagline"),
		meta: [
			t("aboutPages.foundersMessage.hero.meta.inovation"),
			t("aboutPages.foundersMessage.hero.meta.beyondExpectation"),
			t("aboutPages.foundersMessage.hero.meta.longTermView"),
		],

		labels: {
			leadershipInsight: t(
				"aboutPages.foundersMessage.labels.leadershipInsight",
			),
			foundersVision: t("aboutPages.foundersMessage.labels.foundersVision"),
			deskLabel: t("aboutPages.foundersMessage.labels.desk", {
				role: t("aboutPages.foundersMessage.signatory.role"),
			}),
		},

		signatory: {
			name: "Venkatesh Kulkarni",
			role: t("aboutPages.foundersMessage.signatory.role"),
		},

		messageSections: {
			title: t("aboutPages.foundersMessage.hero.title"),
			subtitle: t("aboutPages.foundersMessage.hero.description"),

			heroQuote: t("aboutPages.foundersMessage.heroQuote"),

			sections: [
				{
					type: "origin",
					heading: t("aboutPages.foundersMessage.sections.origin.heading"),
					content: t("aboutPages.foundersMessage.sections.origin.content"),
				},
				{
					type: "philosophy",
					heading: t("aboutPages.foundersMessage.sections.philosophy.heading"),
					content: t("aboutPages.foundersMessage.sections.philosophy.content"),
				},
				{
					type: "scale_and_capability",
					heading: t(
						"aboutPages.foundersMessage.sections.scaleAndCapability.heading",
					),
					content: t(
						"aboutPages.foundersMessage.sections.scaleAndCapability.content",
					),
				},
				{
					type: "innovation",
					heading: t("aboutPages.foundersMessage.sections.innovation.heading"),
					content: t("aboutPages.foundersMessage.sections.innovation.content"),
				},
				{
					type: "ethics",
					heading: t("aboutPages.foundersMessage.sections.ethics.heading"),
					content: t("aboutPages.foundersMessage.sections.ethics.content"),
				},
				{
					type: "closing",
					heading: t("aboutPages.foundersMessage.sections.closing.heading"),
					content: t("aboutPages.foundersMessage.sections.closing.content"),
				},
			],
		},
	};

	const sectionRef = useRef<HTMLDivElement>(null);
	const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
	const controls = useAnimation();
	const shouldReduceMotion = useReducedMotion();
	const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
	useGsapScrollParallax(sectionRef, { y: -80, opacity: 0.92, scale: 0.985 });

	useEffect(() => {
		if (isInView) {
			controls.start("visible");
		}
	}, [isInView, controls]);

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: shouldReduceMotion ? 0 : 0.12,
				delayChildren: 0.1,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 16 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
		},
	};

	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
		if (shouldReduceMotion) {
			return;
		}
		const rect = e.currentTarget.getBoundingClientRect();
		setMousePosition({
			x: ((e.clientX - rect.left) / rect.width) * 100,
			y: ((e.clientY - rect.top) / rect.height) * 100,
		});
	};
	return (
		<PageShell>
			<Seo
				titleKey="aboutPages.foundersMessage.seo.title"
				descriptionKey="aboutPages.foundersMessage.seo.description"
				path="/founder-message"
			/>
			<Navigation />
			{/* HEADER */}{" "}
			<SectionWrapper className={PAGE_HERO_CLASS}>
				<div className="absolute inset-0 ">
					{" "}
					<div
						className="absolute bottom-3 inset-0 opacity-35 scale-105"
						style={{
							backgroundImage: `url(${pageData.bgimage})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#3b0092"
						activeColor="#f09a38"
						proximity={100}
						shockRadius={200}
						shockStrength={3}
						resistance={750}
						returnDuration={1.5}
					/>
				</div>
				<PageHeaderHero
					// align="center"
					title={pageData.title}
					description={pageData.tagline}
					meta={pageData.meta}
				/>
			</SectionWrapper>
			<SectionDivider />
			<FoundersMessageComponent data={pageData} />
			<FooterSection />
		</PageShell>
	);
};

export default FoundersMessage;
